import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #restoreCycle = 0;
  #swipeGesture;
  #swipeDialog;
  #swipeListeners;
  #swipeClickListeners;
  #swipeClickTimer;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartEvent);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    const { dialog } = /** @type {Refs} */ (this.refs);
    this.#swipeListeners?.abort();
    this.#swipeDialog = dialog;
    this.#swipeListeners = new AbortController();
    const { signal } = this.#swipeListeners;
    dialog?.addEventListener('pointerdown', this.#handleSwipeStart, { passive: true, signal });
    dialog?.addEventListener('pointermove', this.#handleSwipeMove, { passive: false, signal });
    dialog?.addEventListener('pointerup', this.#handleSwipeEnd, { passive: false, signal });
    dialog?.addEventListener('pointercancel', this.#resetSwipeGesture, { passive: true, signal });
    queueMicrotask(this.#ensureModalState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartEvent);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.#swipeListeners?.abort();
    this.#swipeListeners = undefined;
    this.#swipeDialog = undefined;
    this.#resetSwipeGesture();
    this.#clearSwipeClick();
  }

  #canSwipe() {
    const dialog = this.#swipeDialog;
    return (
      window.matchMedia('(max-width: 749px)').matches &&
      (window.visualViewport?.scale ?? 1) <= 1.01 &&
      dialog instanceof HTMLDialogElement &&
      dialog.open &&
      dialog.matches(':modal') &&
      !dialog.classList.contains('dialog-closing')
    );
  }

  #handleSwipeStart = (event) => {
    this.#resetSwipeGesture();
    this.#clearSwipeClick();
    const target = event.target instanceof Element ? event.target : null;
    const startsInFormControl = target?.closest(
      'button, a, input, select, textarea, summary, label, video, audio, iframe, [contenteditable], ' +
      '[role="button"], [role="slider"], [role="combobox"], [role="checkbox"], [role="radio"], ' +
      '[tabindex]:not([tabindex="-1"]), [data-dj-cart-recommendations-rail]'
    );

    if (
      !this.#canSwipe() ||
      !event.isPrimary ||
      event.pointerType !== 'touch' ||
      !target ||
      startsInFormControl
    ) {
      return;
    }

    this.#swipeGesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTime: event.timeStamp,
      intent: 'pending',
    };
    // A second contact can start outside the drawer. Leave pinch zoom to the browser.
    window.addEventListener('pointerdown', this.#cancelForAdditionalPointer, { capture: true, passive: true });
  };

  #cancelForAdditionalPointer = (event) => {
    if (event.pointerId !== this.#swipeGesture?.pointerId) this.#resetSwipeGesture();
  };

  #handleSwipeMove = (event) => {
    const gesture = this.#swipeGesture;
    if (!gesture || gesture.pointerId !== event.pointerId || gesture.intent === 'blocked') return;
    if (!this.#canSwipe()) {
      this.#resetSwipeGesture();
      return;
    }

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);

    if (gesture.intent === 'pending') {
      if (Math.max(absoluteX, absoluteY) < 12) return;
      if (deltaX <= 0 || absoluteY >= absoluteX) {
        gesture.intent = 'blocked';
        return;
      }
      if (deltaX <= absoluteY * 1.25) return;
      gesture.intent = 'horizontal';
    }

    if (event.cancelable) event.preventDefault();
  };

  #handleSwipeEnd = (event) => {
    const gesture = this.#swipeGesture;
    this.#resetSwipeGesture();
    if (!gesture || gesture.pointerId !== event.pointerId || gesture.intent !== 'horizontal') return;

    if (!this.#canSwipe()) return;
    const dialog = this.#swipeDialog;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    const duration = Math.max(1, event.timeStamp - gesture.startTime);
    const drawerWidth = dialog.getBoundingClientRect().width || window.innerWidth;
    const distanceThreshold = Math.min(104, Math.max(72, drawerWidth * 0.22));
    const isHorizontal = deltaX > Math.abs(deltaY) * 1.35;
    const isFastFlick = deltaX >= 52 && duration <= 280 && deltaX / duration >= 0.5;

    if (!isHorizontal || (deltaX < distanceThreshold && !isFastFlick)) return;

    if (event.cancelable) event.preventDefault();
    this.#suppressSwipeClick(event);
    this.close(event);
  };

  #resetSwipeGesture = () => {
    this.#swipeGesture = undefined;
    window.removeEventListener('pointerdown', this.#cancelForAdditionalPointer, true);
  };

  #clearSwipeClick = () => {
    this.#swipeClickListeners?.abort();
    this.#swipeClickListeners = undefined;
    window.clearTimeout(this.#swipeClickTimer);
    this.#swipeClickTimer = undefined;
  };

  #suppressSwipeClick(release) {
    this.#clearSwipeClick();
    this.#swipeClickListeners = new AbortController();
    const { signal } = this.#swipeClickListeners;
    const preventCompatibilityClick = (event) => {
      const sameTouch =
        (event.pointerType === 'touch' && event.pointerId === release.pointerId) ||
        (!event.pointerType && event.sourceCapabilities?.firesTouchEvents === true);
      const samePosition =
        Math.abs(event.clientX - release.clientX) < 24 && Math.abs(event.clientY - release.clientY) < 24;
      if (event.detail !== 0 && sameTouch && samePosition) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      this.#clearSwipeClick();
    };

    // Suppress only this gesture's compatibility click, never the customer's next tap.
    this.#swipeDialog?.addEventListener('click', preventCompatibilityClick, { capture: true, signal });
    window.addEventListener('pointerdown', this.#clearSwipeClick, { capture: true, once: true, passive: true, signal });
    this.#swipeClickTimer = window.setTimeout(this.#clearSwipeClick, 450);
  }

  #handleCartEvent = (event) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const wasOpen = Boolean(dialog?.open);
    const source = event.detail?.data?.source;
    const itemCount = Number(event.detail?.data?.itemCount);
    const preserveDrawerModal = event.detail?.data?.preserveDrawerModal === true;
    const isAdd =
      event instanceof CartAddEvent ||
      source === 'product-form-component' ||
      source === 'dj-cart-recommendations';

    if (!isAdd && itemCount === 0) {
      this.#restoreCycle += 1;
      if (wasOpen) this.closeDialog();
      return;
    }

    if (!isAdd && preserveDrawerModal && wasOpen) {
      this.#restoreCycle += 1;
      return;
    }

    if (isAdd) {
      if (!this.hasAttribute('auto-open')) return;
      if (!wasOpen) this.showDialog();
    } else if (!wasOpen) {
      return;
    }

    this.#restoreOpenAfterMorph();
  };

  #ensureModalState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open || dialog.matches(':modal')) return false;

    if (!dialog.querySelector('tbody .cart-items__table-row')) {
      dialog.close();
      return true;
    }

    dialog.close();
    this.showDialog();
    return true;
  };

  #restoreOpenAfterMorph() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const scrollTop = dialog?.querySelector('.cart-drawer__items')?.scrollTop || 0;
    const restoreCycle = ++this.#restoreCycle;
    let attempts = 0;

    const restore = () => {
      if (restoreCycle !== this.#restoreCycle) return;

      const nextDrawer =
        document.querySelector('cart-drawer-component[auto-open]') ||
        document.querySelector('cart-drawer-component');
      const nextDialog = nextDrawer?.querySelector('dialog');

      if (!(nextDialog instanceof HTMLDialogElement)) return;
      if (!nextDialog.matches(':modal') && typeof nextDrawer.showDialog === 'function') {
        if (nextDialog.open) nextDialog.close();
        nextDrawer.showDialog();
      }

      const nextItems = nextDialog.querySelector('.cart-drawer__items');
      if (nextItems instanceof HTMLElement) nextItems.scrollTop = scrollTop;

      attempts += 1;
      if (attempts < 10 && nextDrawer === this && restoreCycle === this.#restoreCycle) {
        window.setTimeout(restore, 50);
      }
    };

    window.requestAnimationFrame(restore);
  }

  #handleOpen = () => {
    this.#updateStickyState();
    this.dispatchEvent(new CustomEvent('dj:cart-drawer-open'));
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.#resetSwipeGesture();
    this.#restoreCycle += 1;
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const summary = dialog.querySelector('.cart-drawer__summary');
    dialog.setAttribute('cart-summary-sticky', summary ? 'true' : 'false');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
